import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames                                                   from 'classnames';
import DV                                                           from 'ts-data-validator';

import useEventListener from '../../../common/hooks/useEventListener';

const StaticTabs = (props) => {
  const {
          activeTab,
          children,
          nav,
          label,
          style,
        } = props;

  const [state, setState] = useState({
    active: activeTab || 0,
    open:   false,
  });

  const [visibleRange, setVisibleRange] = useState({
    start: 0,
    end:   2,
  });
  const [isMobile, setIsMobile]         = useState(false);

  const optionsRef = useRef(null);

  const handleChange = useCallback((active) => {
    if (state.active !== active) {
      setState((prev) => (
        {
          ...prev,
          active,
          open: !state.open,
        }));
    }
  }, [state.active, state.open]);

  const handleTabClick = useCallback((index) => {
    if (index === 0) {
      setVisibleRange({
        start: index,
        end:   2,
      });
    } else if (index >= nav.length - 1) {
      setVisibleRange({
        start: index - 1,
        end:   index + 1,
      });
    } else if (index > visibleRange.start) {
      setVisibleRange({
        start: index,
        end:   index + 2,
      });
    } else if (index < visibleRange.end) {
      setVisibleRange({
        start: index - 1,
        end:   index + 1,
      });
    }
    handleChange(index);
  }, [nav.length, visibleRange, handleChange]);

  const handleResize = useCallback(() => {
    const { screen: { width } } = window;
    if (width <= 990) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, []);

  useEventListener('resize', handleResize);

  const visibleTabs = useMemo(() => {
    return isMobile
      ? Object.values(nav)
        .slice(visibleRange.start, visibleRange.end)
      : Object.values(nav);
  }, [nav, visibleRange, isMobile]);

  const handleOpen = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    setState((prev) => (
      {
        ...prev,
        open: !state.open,
      }));
  }, [state.open]);

  const handleClickOutside = useCallback((event) => {
    if (
      state.open &&
      (
        optionsRef.current && !optionsRef.current.contains(event.target))
    ) {
      handleOpen(event);
    }
  }, [state.open, handleOpen]);

  useEffect(() => {
    if (state.open) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [state.open]);

  useEffect(() => {
    handleResize();
  }, []);

  return (
    <div className={classNames('tabbable static', style, { 'open': state.open })}>
      <div className={'top'}>
        {DV.isString(label) && (
          <div className={'title'}>{label}</div>)}
        <div className={'body'}>
          <ul ref={optionsRef} className={classNames('tabs basic-tabs')}>
            {visibleTabs && visibleTabs.filter(tab => DV.isNotEmptyString(tab))
              .map((tab, index) => (
                <li
                  key={index}
                  className={classNames({
                    'active': isMobile
                                ? state.active === index + visibleRange.start
                                : state.active === index,
                  })}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (isMobile) {
                      handleTabClick(index + visibleRange.start);
                    } else {
                      handleTabClick(index);
                    }
                  }}
                >{tab}</li>
              ))}
          </ul>
        </div>
      </div>
      <div className={'tab-content'}>
        <div className={'tab-pane active'}>
          {children[state.active] || null}
        </div>
      </div>
    </div>
  );
};

export default StaticTabs;